// src/pages/ApprovedBusiness.jsx
import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { Link } from "react-router-dom"

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  InputGroup,
  InputGroupText,
  Table,
  Input,
  Spinner,
  Form,
  FormGroup,
  Label,
} from "reactstrap"
import TableContainer from "../../components/Common/TableContainer"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import apiURl, { baseURl } from "url"
import classnames from "classnames"
import ExcelDownload from "components/Common/ExcelDownload"

const ApprovedBusiness = () => {
  document.title = "ECS Business Management — Approved"
  const navigate = useNavigate()

  // Auth
  const role_ = localStorage.getItem("role")
  useEffect(() => {
    if (role_ !== "admin") navigate("/")
  }, [role_, navigate])

  // State
  const [businesses, setBusinesses] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  // Details modal
  const [modal, setModal] = useState(false)
  const [selectedBusiness, setSelectedBusiness] = useState(null)
  const [showPassword, setShowPassword] = useState(false)

  // New Business modal (kept from your original file; remove if you don’t need it on this page)
  const [businessModal, setBusinessModal] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [formData, setFormData] = useState({
    businessName: "",
    businessPhone: "",
    businessAddress: "",
    businessCity: "",
    businessNTN: "",
    accountName: "",
    accountMail: "",
    accountPassword: "",
  })

  // Filters
  const [filters, setFilters] = useState({
    name: "",
    city: "",
    dateFrom: "",
    dateTo: "",
  })

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const resetForm = () => {
    setFormData({
      businessName: "",
      businessPhone: "",
      businessAddress: "",
      businessCity: "",
      businessNTN: "",
      accountName: "",
      accountMail: "",
      accountPassword: "",
    })
    setBusinessModal(false)
  }

  const handleSubmitBusiness = async e => {
    setSubmitLoading(true)
    e.preventDefault()
    const required = [
      "businessName",
      "businessPhone",
      "businessAddress",
      "businessCity",
      "accountName",
      "accountMail",
      "accountPassword",
    ]
    for (let f of required) {
      if (!formData[f]) {
        toastr.error(`${f} is required`)
        setSubmitLoading(false)
        return
      }
    }
    try {
      const res = await axios.post(`${apiURl}/registerBusiness`, formData)
      if (res.data.status === "success") {
        toastr.success("Business registered successfully")
        resetForm()
        getAllBusinesses()
      } else {
        toastr.error(res.data.message || "Failed to register business")
      }
    } catch (err) {
      console.error(err)
      toastr.error("Server error during business registration")
    } finally {
      setSubmitLoading(false)
    }
  }

  // Data
  const getAllBusinesses = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get(`${apiURl}/getAllBusinesses`)
      if (response.data.status === "success") {
        setBusinesses(response.data.businesses || [])
      } else {
        console.error("Failed to fetch businesses:", response.data.message)
      }
    } catch (error) {
      console.error("Error fetching businesses:", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getAllBusinesses()
  }, [])

  //Detail
  const [activeDocTab, setActiveDocTab] = useState("cnicFront")

  const toggleDocTab = tab => {
    if (activeDocTab !== tab) setActiveDocTab(tab)
  }

  const documents = [
    {
      key: "cnicFront",
      label: "CNIC Front",
      url: selectedBusiness?.frontCnicImage,
    },
    {
      key: "cnicBack",
      label: "CNIC Back",
      url: selectedBusiness?.backCnicImage,
    },
    {
      key: "ntn",
      label: "NTN License",
      url: selectedBusiness?.ntnImage,
    },
    {
      key: "blankCheck",
      label: "Blank Check",
      url: selectedBusiness?.blankChequeImage,
    },
  ]

  // Only APPROVED
  const approvedBusinesses = useMemo(
    () => businesses.filter(b => b?.accountStatus === "Approved"),
    [businesses]
  )

  // Apply filters (name, city, createdAt range)
  const filteredApproved = useMemo(() => {
    const name = filters.name.trim().toLowerCase()
    const city = filters.city.trim().toLowerCase()
    const from = filters.dateFrom ? new Date(filters.dateFrom) : null
    const to = filters.dateTo ? new Date(filters.dateTo) : null
    if (to) to.setHours(23, 59, 59, 999)

    return approvedBusinesses.filter(b => {
      // businessName
      if (name && !(b.businessName || "").toLowerCase().includes(name))
        return false
      // businessCity
      if (city && !(b.businessCity || "").toLowerCase().includes(city))
        return false
      // createdAt date range
      if (from || to) {
        const created = new Date(b.createdAt)
        if (!isNaN(created)) {
          if (from && created < from) return false
          if (to && created > to) return false
        }
      }
      return true
    })
  }, [approvedBusinesses, filters])

  const toggleModal = business => {
    if (business) setSelectedBusiness(business)
    setShowPassword(false)
    setModal(true) // open
  }

  // Columns
  const baseColumns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        disableFilters: true,
        Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
      },
      {
        Header: "Business Name",
        accessor: "businessName",
        disableFilters: true,
        Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
      },
      {
        Header: "Email",
        accessor: "accountMail",
        disableFilters: true,
        Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
      },
      {
        Header: "Phone",
        accessor: "businessPhone",
        disableFilters: true,
        Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
      },
      {
        Header: "NTN",
        accessor: "businessNTN",
        disableFilters: true,
        Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
      },
      {
        Header: "Created Date",
        accessor: "createdAt",
        disableFilters: true,
        Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableFilters: true,
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => toggleModal(row.original)}
            >
              Detail
            </button>
            <Link
              to={`/cod-businesses/${row.original.id}/rates`}
              className="btn btn-warning btn-sm"
              title="Open Rate Card"
            >
              Rate Card
            </Link>
            <Link
              to={`/cod-business/wallet/${row.original.id}`}
              className="btn btn-success btn-sm"
              title="Wallet"
            >
              <i className="bx bx-dollar"></i>Wallet
            </Link>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="align-items-center">
          <Col xl="6">
            <h5 className="mt-2">Approved Businesses</h5>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="g-3 mt-3">
          <Col md="4">
            <Label className="form-label mb-1">Business Name</Label>
            <Input
              placeholder="Search by name"
              value={filters.name}
              onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
            />
          </Col>
          <Col md="4">
            <Label className="form-label mb-1">Business City</Label>
            <Input
              placeholder="Search by city"
              value={filters.city}
              onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
            />
          </Col>
          <Col md="4">
            <Label className="form-label mb-1">Created Date (Range)</Label>
            <div className="d-flex gap-2">
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={e =>
                  setFilters(f => ({ ...f, dateFrom: e.target.value }))
                }
              />
              <Input
                type="date"
                value={filters.dateTo}
                onChange={e =>
                  setFilters(f => ({ ...f, dateTo: e.target.value }))
                }
              />
              <Button
                color="secondary"
                outline
                onClick={() =>
                  setFilters({ name: "", city: "", dateFrom: "", dateTo: "" })
                }
              >
                Clear
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="pt-3">
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: 200 }}
                  >
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <div className="table-responsive">
                    <div className="float-end d-flex gap-2">
                      <div className="mt-3">
                        <ExcelDownload
                          leads={filteredApproved}
                          title="Approved Business"
                        />
                      </div>
                    </div>
                    <TableContainer
                      columns={baseColumns}
                      data={filteredApproved}
                      isGlobalFilter={true}
                      customPageSize={10}
                      className="custom-table"
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Details Modal */}
      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          Business Details
        </ModalHeader>

        <ModalBody>
          {selectedBusiness && (
            <>
              <Row>
                {/* Left: Contact */}
                <Col md="6">
                  <Card className="mb-3">
                    <CardHeader>Contact</CardHeader>
                    <CardBody className="pt-0">
                      <Table bordered size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <th style={{ width: 140 }}>ID</th>
                            <td>{selectedBusiness.id || "—"}</td>
                          </tr>
                          <tr>
                            <th>Business Name</th>
                            <td>{selectedBusiness.businessName || "—"}</td>
                          </tr>
                          <tr>
                            <th>Email</th>
                            <td>
                              {selectedBusiness.accountMail ? (
                                <a
                                  href={`mailto:${selectedBusiness.accountMail}`}
                                >
                                  {selectedBusiness.accountMail}
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th>Phone</th>
                            <td>
                              {selectedBusiness.businessPhone ? (
                                <a
                                  href={`tel:${selectedBusiness.businessPhone}`}
                                >
                                  {selectedBusiness.businessPhone}
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th>Password</th>
                            <td>
                              <InputGroup style={{ maxWidth: 260 }}>
                                <Input
                                  type={showPassword ? "text" : "password"}
                                  value={selectedBusiness.accountPassword || ""}
                                  readOnly
                                  className="form-control-sm"
                                />
                                <InputGroupText
                                  style={{ cursor: "pointer" }}
                                  onClick={() => setShowPassword(s => !s)}
                                  title={showPassword ? "Hide" : "Show"}
                                >
                                  <FontAwesomeIcon
                                    icon={showPassword ? faEyeSlash : faEye}
                                  />
                                </InputGroupText>
                              </InputGroup>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </Col>

                {/* Right: Business */}
                <Col md="6">
                  <Card className="mb-3">
                    <CardHeader>Business</CardHeader>
                    <CardBody className="pt-0">
                      <Table bordered size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <th style={{ width: 140 }}>NTN</th>
                            <td>{selectedBusiness.businessNTN || "—"}</td>
                          </tr>
                          <tr>
                            <th>Address</th>
                            <td>{selectedBusiness.businessAddress || "—"}</td>
                          </tr>
                          <tr>
                            <th>City</th>
                            <td>{selectedBusiness.businessCity || "—"}</td>
                          </tr>
                          <tr>
                            <th>Status</th>
                            <td>{selectedBusiness.accountStatus || "—"}</td>
                          </tr>
                          <tr>
                            <th>Created Date</th>
                            <td>{selectedBusiness.createdAt || "—"}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="12">
                  <h5>
                    <b>Documents</b>
                  </h5>

                  {/* Tabs */}
                  <Nav tabs>
                    {documents.map(doc => (
                      <NavItem key={doc.key}>
                        <NavLink
                          className={classnames({
                            active: activeDocTab === doc.key,
                          })}
                          onClick={() => toggleDocTab(doc.key)}
                          style={{ cursor: "pointer" }}
                        >
                          {doc.label}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  {/* Tab Content */}
                  <TabContent
                    activeTab={activeDocTab}
                    className="p-3 border border-top-0 rounded-bottom"
                  >
                    {documents.map(doc => (
                      <TabPane key={doc.key} tabId={doc.key}>
                        {doc.url ? (
                          <>
                            <div
                              style={{
                                width: "100%",
                                maxWidth: "300px",
                                height: "300px", // ✅ fixed height
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #dee2e6",
                                borderRadius: "5px",
                                backgroundColor: "#f8f9fa",
                              }}
                            >
                              <img
                                src={`${baseURl}${doc.url}`}
                                alt={doc.label}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                            <div className="d-flex gap-2 mt-2">
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() =>
                                  window.open(`${baseURl}${doc.url}`, "_blank")
                                }
                              >
                                View Full
                              </Button>
                            </div>
                          </>
                        ) : (
                          <p className="text-muted">
                            No image available for this document.
                          </p>
                        )}
                      </TabPane>
                    ))}
                  </TabContent>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Register New Business Modal (optional on this page) */}
      <Modal
        isOpen={businessModal}
        toggle={() => setBusinessModal(v => !v)}
        size="lg"
        centered
      >
        <Form onSubmit={handleSubmitBusiness}>
          <ModalHeader toggle={() => setBusinessModal(v => !v)}>
            Register New Business
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Business Name *</Label>
                  <Input
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Business Phone *</Label>
                  <Input
                    name="businessPhone"
                    value={formData.businessPhone}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Business City *</Label>
                  <Input
                    name="businessCity"
                    value={formData.businessCity}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Account Name *</Label>
                  <Input
                    name="accountName"
                    value={formData.accountName}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Business Address *</Label>
                  <Input
                    name="businessAddress"
                    value={formData.businessAddress}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Business NTN (optional)</Label>
                  <Input
                    name="businessNTN"
                    value={formData.businessNTN}
                    onChange={handleInputChange}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Account Email *</Label>
                  <Input
                    type="email"
                    name="accountMail"
                    value={formData.accountMail}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Account Password *</Label>
                  <Input
                    type="password"
                    name="accountPassword"
                    value={formData.accountPassword}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={resetForm}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitLoading}>
              {submitLoading ? "Saving..." : "Register Business"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {submitLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div className="text-center text-white">
            <i
              className="fas fa-spinner fa-spin"
              style={{ fontSize: "3rem", marginBottom: "1rem" }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ApprovedBusiness
